import { useNavigate } from "@solidjs/router";
import { createEffect, For, onCleanup, Show } from "solid-js";
import { comingSoon } from "./ComingSoon";

interface Entry {
  icon: string;
  label: string;
  /** Route to open; none: "Bientôt disponible". */
  href?: string;
}

/** Figma H1 rows, in order. Only the history and the customer area exist in the PoC. */
const ENTRIES: Entry[] = [
  { icon: "menu-qr-scan", label: "E-tickets et Pass gratuité" },
  { icon: "menu-person", label: "Espace client", href: "/account" },
  { icon: "menu-card", label: "Moyens de paiement" },
  { icon: "menu-info", label: "Info trafic" },
  { icon: "menu-bell", label: "Messages" },
  { icon: "menu-star", label: "Demander un Pass gratuité" },
  { icon: "menu-history", label: "Historique d'achat", href: "/historique" },
  { icon: "menu-calendar", label: "Résa'TaM, transport à la demande" },
];

/**
 * Figma H1 M'Ticket menu: scrim (black 45 %) and a white drawer from the right, 303 px wide with
 * 16 px left corners, 86 px under the safe area (140 - 54 px status bar), 34 px above the bottom.
 * Rows: 26 px icon at 21 px, Medium 13.5 #49454f label at 59 px, 50 px pitch, 287 x 42 hotspots.
 * Tapping the scrim or Escape closes it. Screens that call comingSoon mount ComingSoonToast.
 */
export function MenuDrawer(props: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const go = (e: Entry) => {
    if (!e.href) return comingSoon();
    props.onClose();
    navigate(e.href);
  };
  let panel: HTMLDivElement | undefined;
  createEffect(() => {
    if (!props.open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") props.onClose();
    };
    document.addEventListener("keydown", onKey);
    queueMicrotask(() => panel?.focus());
    onCleanup(() => document.removeEventListener("keydown", onKey));
  });
  return (
    <Show when={props.open}>
      <div class="fixed inset-0 z-40 mx-auto max-w-[430px]">
        <button
          type="button"
          class="absolute inset-0 bg-black/45"
          aria-label="Fermer le menu"
          onClick={() => props.onClose()}
        />
        <div
          ref={panel}
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          tabindex="-1"
          class="absolute right-0 flex w-[303px] max-w-[calc(100%-56px)] flex-col overflow-y-auto rounded-l-[16px] bg-white outline-none"
          style={{
            top: "calc(env(safe-area-inset-top) + 86px)",
            bottom: "max(34px, env(safe-area-inset-bottom))",
          }}
        >
          <nav class="flex flex-col gap-2 px-2 pt-[22px]">
            <For each={ENTRIES}>
              {(e) => (
                <button
                  type="button"
                  class="flex h-[42px] w-full items-center gap-3 rounded-[8px] pl-[13px] pr-2 text-left active:bg-surface2"
                  onClick={() => go(e)}
                >
                  <img src={`/icons/${e.icon}.svg`} width="26" height="26" alt="" />
                  <span class="text-[13.5px] font-medium leading-[1.25] tracking-[0.135px] text-strong">
                    {e.label}
                  </span>
                </button>
              )}
            </For>
          </nav>
          <div class="mt-auto px-[21px] pb-[18px] pt-6">
            <div class="flex items-center">
              <button
                type="button"
                class="-ml-1 flex h-[42px] w-[108px] items-center gap-[6px] px-1"
                onClick={() => comingSoon()}
              >
                <img src="/icons/menu-doc.svg" width="24" height="24" alt="" />
                <span class="text-[13.5px] font-medium leading-[1.25] tracking-[0.135px] text-strong">
                  CGV
                </span>
              </button>
              <button
                type="button"
                class="flex h-[42px] items-center gap-[6px] px-1"
                onClick={() => comingSoon()}
              >
                <img src="/icons/menu-mail.svg" width="24" height="24" alt="" />
                <span class="text-[13.5px] font-medium leading-[1.25] tracking-[0.135px] text-strong">
                  Nous contacter
                </span>
              </button>
            </div>
            <div class="mt-[15px] flex gap-[10px] pl-[2px]">
              <button
                type="button"
                class="h-8 w-[92px] rounded-[16px] border border-text bg-white text-[13px] font-medium leading-[1.25] tracking-[0.13px] text-strong"
                onClick={() => comingSoon()}
              >
                English
              </button>
              <button
                type="button"
                aria-pressed="true"
                class="h-8 w-[102px] rounded-[16px] border-2 border-violet bg-surface2 text-[13px] font-medium leading-[1.25] tracking-[0.13px] text-strong"
              >
                Français
              </button>
            </div>
            <p class="mt-[34px] text-right text-[13px] leading-[1.25] tracking-[0.13px] text-strong">
              Version : 2.14.1
            </p>
          </div>
        </div>
      </div>
    </Show>
  );
}
